import { useCallback, useEffect, useMemo } from "react"
import { useController, useFormContext } from "react-hook-form"
import { defineMessages, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import type { DatepickerProps } from "./Datepicker"
import { Datepicker } from "./Datepicker"

export interface DatePickerFieldProps extends Omit<DatepickerProps, "value"> {
	name: string
	shouldUnregister?: boolean
}

const messages = defineMessages({
	maxDateError: {
		defaultMessage: "Too far in the future",
		id: "form.error.dateMax",
	},
	minDateError: {
		defaultMessage: "Too far in the past",
		id: "form.error.dateMin",
	},
})

export const DatepickerField = (props: DatePickerFieldProps) => {
	const { disabledHint, label, maxDate, minDate, name, required, shouldUnregister = true, state, ...rest } = props
	const { formatMessage } = useIntl()

	const { control, trigger } = useFormContext()

	const rules = useMemo(() => {
		return {
			max: maxDate
				? {
						message: formatMessage(messages.maxDateError),
						value: maxDate,
					}
				: undefined,
			min: minDate
				? {
						message: formatMessage(messages.minDateError),
						value: minDate,
					}
				: undefined,
			required: {
				message: formatMessage(formMessages.required),
				value: !!required,
			},
		}
	}, [required, minDate, maxDate, formatMessage])

	const {
		field: { onChange, ref, value },
		fieldState: { error, isDirty },
		formState: { isSubmitted },
	} = useController({
		control,
		name,
		rules,
		shouldUnregister,
	})

	const handleChange = useCallback(
		(newValue: { input: string; isoDate: string | null; isValid: boolean }) => {
			if (isSubmitted && !newValue.isValid) {
				control.setError(name, { message: formatMessage(formMessages.invalid), type: "manual" })
			}
			if (!value && newValue.isoDate === null) {
				return
			}
			onChange(newValue.isoDate)
		},
		[onChange, value, formatMessage, control, name, isSubmitted],
	)

	// Fields need to retrigger validation in order to apply changed rules, since the rules are cached inside react-hook-form.
	// See https://github.com/react-hook-form/react-hook-form/issues/1749 and https://github.com/react-hook-form/react-hook-form/issues/8310
	// An error of the server is kept until the user changes the field, validating again would remove it.
	const hasServerError = error?.type === "server"
	useEffect(() => {
		if ((isDirty || isSubmitted) && !hasServerError) {
			trigger(name)
		}
	}, [rules, trigger, name, isDirty, isSubmitted, hasServerError])

	return (
		<Datepicker
			{...rest}
			name={name}
			label={label}
			value={value}
			ref={ref}
			minDate={minDate}
			maxDate={maxDate}
			disabledHint={disabledHint}
			required={!!required}
			state={error ? "error" : state}
			message={error?.message}
			onChange={handleChange}
		/>
	)
}
